import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "lib/utils";
import { Loader } from "components/ui/loader";

type Variant =
  | "primary"
  | "secondary"
  | "ghost"
  | "destructive"
  | "outline"; // ✅ added

type Size = "sm" | "md" | "lg" | "icon";
type LoadingPlacement = "start" | "end" | "replace";

const VARIANT: Record<Variant, string> = {
  primary: "btn-primary",
  secondary: "btn-secondary",
  ghost: "btn-ghost",
  destructive: "btn-destructive",

  // ✅ New Outline variant
  outline: cn(
    "border border-line-strong dark:border-gray-600 bg-transparent",
    "text-gray-800 dark:text-gray-200",
    "hover:bg-gray-100 dark:hover:bg-gray-800",
    "focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-500",
    "transition-colors duration-200"
  ),
};

const SIZE: Record<Size, string> = {
  sm: "h-9 px-3 py-2 text-sm",
  md: "", // defaults from btn-* classes (px-6 py-2.5)
  lg: "h-11 px-8 py-3 text-base",
  icon: "h-10 w-10 p-0",
};

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  loadingPlacement?: LoadingPlacement;
  spinner?: React.ReactNode;
  fullWidth?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "primary",
      size = "md",
      loading = false,
      loadingPlacement = "start",
      spinner,
      asChild,
      fullWidth,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    const isDisabled = disabled || loading;

    const loaderNode = spinner ?? <Loader size={18} label="Processing" />;

    const showStart = loading && loadingPlacement === "start";
    const showEnd = loading && loadingPlacement === "end";
    const replace = loading && loadingPlacement === "replace";

    return (
      <Comp
        ref={ref}
        className={cn(
          "inline-flex items-center justify-center gap-2 rounded-full font-medium focus:outline-none",
          VARIANT[variant],
          SIZE[size],
          fullWidth && "w-full",
          isDisabled && "opacity-70 cursor-not-allowed",
          className
        )}
        disabled={isDisabled}
        aria-disabled={isDisabled || undefined}
        aria-busy={loading || undefined}
        data-loading={loading || undefined}
        {...props}
      >
        {replace ? (
          loaderNode
        ) : (
          <>
            {showStart && loaderNode}
            {/* gap-2 here, not only on the button root: the root's gap sits
                between the loader and this span, so an icon passed alongside a
                label — <Button><Plus/>Create Campaign</Button>, and every
                `icon` prop on Primary/SecondaryButton — was rendering flush
                against its text. 38 buttons app-wide; the 10 that looked right
                had a hand-written `mr-2` on the icon, since removed. */}
            <span className="inline-flex items-center gap-2">{children}</span>
            {showEnd && loaderNode}
          </>
        )}
      </Comp>
    );
  }
);

Button.displayName = "Button";

export default Button;
